import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Download, Filter, X, TrendingUp, AlertTriangle, Clock, CheckCircle, ArrowLeft } from 'lucide-react';
import { getSessionItem } from '@/utils/sessionManager';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  ON_TIME: { bg: 'bg-green-100', text: 'text-green-800', chart: '#22c55e' },
  EARLY: { bg: 'bg-blue-100', text: 'text-blue-800', chart: '#3b82f6' },
  DELAYED: { bg: 'bg-red-100', text: 'text-red-800', chart: '#ef4444' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', chart: '#eab308' },
  AT_RISK: { bg: 'bg-orange-100', text: 'text-orange-800', chart: '#f97316' },
  OVERDUE: { bg: 'bg-red-200', text: 'text-red-900', chart: '#991b1b' },
};

const OrderTrackingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [drillVendor, setDrillVendor] = useState(null);

  // Vendor portal detection using state
  const [loggedInVendorId, setLoggedInVendorId] = useState(null);
  const [isVendorUser, setIsVendorUser] = useState(false);
  const [vendorDetected, setVendorDetected] = useState(false);

  useEffect(() => {
    try {
      const uType = getSessionItem('userType');
      if (uType === 'vendor') {
        const userRaw = getSessionItem('user');
        const userObj = userRaw ? JSON.parse(userRaw) : null;
        if (userObj?.vendor_id) {
          setLoggedInVendorId(userObj.vendor_id);
          setIsVendorUser(true);
        } else {
          const viRaw = getSessionItem('vendorInfo');
          const vi = viRaw ? JSON.parse(viRaw) : null;
          if (vi?.id) {
            setLoggedInVendorId(vi.id);
            setIsVendorUser(true);
          }
        }
      }
    } catch (e) { /* ignore */ }
    setVendorDetected(true);
  }, []);

  useEffect(() => { fetchCustomers(); }, []);
  useEffect(() => { if (vendorDetected) fetchTrackingData(); }, [customerFilter, startDate, endDate, vendorDetected, loggedInVendorId]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/api/customers`);
      setCustomers(res.data);
    } catch (err) { console.error('Error fetching customers:', err); }
  };

  const fetchTrackingData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (customerFilter && customerFilter !== 'all') params.append('customer_id', customerFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (loggedInVendorId) params.append('vendor_id', loggedInVendorId);
      const response = await axios.get(`${API}/api/reports/order-tracking-summary?${params.toString()}`);
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch tracking data');
    } finally { setLoading(false); }
  };

  // ---- Derived data ----
  const filteredOrders = useMemo(() => {
    if (!data) return [];
    let orders = data.orders;
    if (statusFilter !== 'ALL') orders = orders.filter(o => o.status === statusFilter);
    if (vendorFilter !== 'all') orders = orders.filter(o => o.vendor_name === vendorFilter);
    if (drillVendor) orders = orders.filter(o => o.vendor_name === drillVendor);
    return orders;
  }, [data, statusFilter, vendorFilter, drillVendor]);

  // Vendor performance: group by vendor (uses filtered orders)
  const vendorPerformance = useMemo(() => {
    if (!filteredOrders.length) return [];
    const vendorMap = {};
    filteredOrders.forEach(o => {
      const v = o.vendor_name || 'Unknown';
      if (!vendorMap[v]) vendorMap[v] = { vendor: v, on_time: 0, early: 0, delayed: 0, pending: 0, at_risk: 0, overdue: 0, total: 0 };
      vendorMap[v][o.status.toLowerCase()] = (vendorMap[v][o.status.toLowerCase()] || 0) + 1;
      vendorMap[v].total += 1;
    });
    return Object.values(vendorMap).sort((a, b) => b.total - a.total);
  }, [filteredOrders]);

  // Unique vendors for filter
  const uniqueVendors = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.orders.map(o => o.vendor_name).filter(Boolean))].sort();
  }, [data]);

  // Lead time trends: group completed orders by order_date (weekly buckets) - uses filtered orders
  const leadTimeTrends = useMemo(() => {
    if (!filteredOrders.length) return [];
    const completedOrders = filteredOrders
      .filter(o => o.received_date && o.lead_time_days != null && o.order_date)
      .sort((a, b) => a.order_date.localeCompare(b.order_date));

    if (completedOrders.length === 0) return [];

    // Group by week
    const weekMap = {};
    completedOrders.forEach(o => {
      const d = new Date(o.order_date);
      const weekStart = new Date(d);
      weekStart.setDate(d.getDate() - d.getDay());
      const key = weekStart.toISOString().split('T')[0];
      if (!weekMap[key]) weekMap[key] = { week: key, totalLead: 0, count: 0, totalVariance: 0 };
      weekMap[key].totalLead += o.lead_time_days;
      weekMap[key].totalVariance += (o.days_variance || 0);
      weekMap[key].count += 1;
    });

    return Object.values(weekMap).map(w => ({
      week: w.week,
      avg_lead_time: Math.round(w.totalLead / w.count * 10) / 10,
      avg_variance: Math.round(w.totalVariance / w.count * 10) / 10,
      orders: w.count
    })).sort((a, b) => a.week.localeCompare(b.week));
  }, [filteredOrders]);

  // Pie chart data (uses filtered orders)
  const pieChartData = useMemo(() => {
    if (!filteredOrders.length) return [];
    const counts = { ON_TIME: 0, EARLY: 0, DELAYED: 0, PENDING: 0, AT_RISK: 0, OVERDUE: 0 };
    filteredOrders.forEach(o => { if (counts[o.status] !== undefined) counts[o.status]++; });
    return [
      { name: 'On Time', value: counts.ON_TIME, color: STATUS_COLORS.ON_TIME.chart },
      { name: 'Early', value: counts.EARLY, color: STATUS_COLORS.EARLY.chart },
      { name: 'Delayed', value: counts.DELAYED, color: STATUS_COLORS.DELAYED.chart },
      { name: 'Pending', value: counts.PENDING, color: STATUS_COLORS.PENDING.chart },
      { name: 'At Risk', value: counts.AT_RISK, color: STATUS_COLORS.AT_RISK.chart },
      { name: 'Overdue', value: counts.OVERDUE, color: STATUS_COLORS.OVERDUE.chart },
    ].filter(d => d.value > 0);
  }, [filteredOrders]);

  // Urgency breakdown (uses filtered orders)
  const urgencyData = useMemo(() => {
    if (!filteredOrders.length) return [];
    const pending = filteredOrders.filter(o => ['PENDING', 'AT_RISK', 'OVERDUE'].includes(o.status));
    return [
      { name: 'CRITICAL (0-1d)', count: pending.filter(o => o.status === 'OVERDUE' || (o.days_until_needed != null && o.days_until_needed <= 1)).length, color: '#dc2626' },
      { name: 'URGENT (2-3d)', count: pending.filter(o => o.days_until_needed != null && o.days_until_needed >= 2 && o.days_until_needed <= 3).length, color: '#f97316' },
      { name: 'NORMAL (4-7d)', count: pending.filter(o => o.days_until_needed != null && o.days_until_needed >= 4 && o.days_until_needed <= 7).length, color: '#eab308' },
      { name: 'PLANNED (7+d)', count: pending.filter(o => o.days_until_needed != null && o.days_until_needed > 7).length, color: '#22c55e' },
    ];
  }, [filteredOrders]);

  // On-time rate (uses filtered orders)
  const onTimeRate = useMemo(() => {
    if (!filteredOrders.length) return 0;
    const onTime = filteredOrders.filter(o => o.status === 'ON_TIME').length;
    const early = filteredOrders.filter(o => o.status === 'EARLY').length;
    const delayed = filteredOrders.filter(o => o.status === 'DELAYED').length;
    const completed = onTime + early + delayed;
    if (completed === 0) return 0;
    return Math.round(((onTime + early) / completed) * 100);
  }, [filteredOrders]);

  // Export CSV
  const handleExport = () => {
    if (!filteredOrders.length) return;
    const headers = ['Status', 'PO Number', 'Shipment', 'Item', 'Item Code', 'Vendor', 'Order Date', 'Need Date', 'Received Date', 'Lead Time', 'Qty', 'Result'];
    const rows = filteredOrders.map(o => [
      o.status, o.po_number, o.shipment_number, o.item_name, o.item_code,
      o.vendor_name, o.order_date, o.need_date, o.received_date || '',
      o.lead_time_days || '', o.qty || '', o.status_message
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-tracking-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setStatusFilter('ALL');
    setCustomerFilter('all');
    setVendorFilter('all');
    setStartDate('');
    setEndDate('');
    setDrillVendor(null);
  };

  const hasActiveFilters = statusFilter !== 'ALL' || customerFilter !== 'all' || vendorFilter !== 'all' || startDate || endDate || drillVendor;

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-lg text-slate-500">Loading order tracking data...</div>;
  }

  if (error) {
    return <div className="p-6"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>;
  }

  return (
    <div className="space-y-6" data-testid="order-tracking-dashboard">
      {/* Header + Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Order Tracking Dashboard</h2>
            <p className="text-slate-500 text-sm">Delivery performance, vendor analytics, and lead time trends</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={filteredOrders.length === 0} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-all-filters">
                <X className="w-4 h-4 mr-1" /> Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-700">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Customer</label>
              <Select value={customerFilter} onValueChange={(v) => { setCustomerFilter(v); setDrillVendor(null); }}>
                <SelectTrigger className="h-9" data-testid="order-tracking-customer-filter">
                  <SelectValue placeholder="All customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!isVendorUser && (
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Vendor</label>
              <Select value={vendorFilter} onValueChange={(v) => { setVendorFilter(v); setDrillVendor(null); }}>
                <SelectTrigger className="h-9" data-testid="order-tracking-vendor-filter">
                  <SelectValue placeholder="All vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vendors</SelectItem>
                  {uniqueVendors.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Start Date</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9" data-testid="filter-start-date" />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">End Date</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9" data-testid="filter-end-date" />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9" data-testid="order-tracking-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  <SelectItem value="ON_TIME">On Time</SelectItem>
                  <SelectItem value="EARLY">Early</SelectItem>
                  <SelectItem value="DELAYED">Delayed</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="AT_RISK">At Risk</SelectItem>
                  <SelectItem value="OVERDUE">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { key: 'ALL', label: 'Total', value: data.total, color: 'text-slate-900', icon: TrendingUp },
          { key: 'ON_TIME', label: 'On Time', value: data.summary.on_time, color: 'text-green-600', icon: CheckCircle },
          { key: 'EARLY', label: 'Early', value: data.summary.early, color: 'text-blue-600', icon: CheckCircle },
          { key: 'DELAYED', label: 'Delayed', value: data.summary.delayed, color: 'text-red-600', icon: AlertTriangle },
          { key: 'PENDING', label: 'Pending', value: data.summary.pending, color: 'text-yellow-600', icon: Clock },
          { key: 'AT_RISK', label: 'At Risk', value: data.summary.at_risk, color: 'text-orange-600', icon: AlertTriangle },
          { key: 'OVERDUE', label: 'Overdue', value: data.summary.overdue, color: 'text-red-800', icon: AlertTriangle },
        ].map(({ key, label, value, color, icon: Icon }) => (
          <Card
            key={key}
            className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === key ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setStatusFilter(statusFilter === key ? 'ALL' : key)}
            data-testid={`kpi-card-${key.toLowerCase()}`}
          >
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-2xl font-bold ${color}`}>{value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                </div>
                <Icon className={`w-5 h-5 ${color} opacity-50`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* On-Time Rate Banner */}
      <div className={`rounded-lg p-4 flex items-center justify-between ${onTimeRate >= 80 ? 'bg-green-50 border border-green-200' : onTimeRate >= 50 ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex items-center gap-3">
          <div className={`text-3xl font-bold ${onTimeRate >= 80 ? 'text-green-700' : onTimeRate >= 50 ? 'text-yellow-700' : 'text-red-700'}`}>
            {onTimeRate}%
          </div>
          <div>
            <div className="font-medium text-slate-800">On-Time Delivery Rate</div>
            <div className="text-xs text-slate-500">Based on {filteredOrders.filter(o => ['ON_TIME', 'EARLY', 'DELAYED'].includes(o.status)).length} completed deliveries</div>
          </div>
        </div>
        <Badge className={onTimeRate >= 80 ? 'bg-green-100 text-green-800' : onTimeRate >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
          {onTimeRate >= 80 ? 'Good' : onTimeRate >= 50 ? 'Needs Improvement' : 'Critical'}
        </Badge>
      </div>

      {/* Charts Row 1: Pie + Urgency */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Delivery Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieChartData} cx="50%" cy="50%" outerRadius={80} innerRadius={40} dataKey="value"
                    label={({ name, value, percent, cx, cy, midAngle, outerRadius }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 25;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text x={x} y={y} fill="#334155" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11}>
                          {name}: {value} ({(percent * 100).toFixed(0)}%)
                        </text>
                      );
                    }} labelLine={true}>
                    {pieChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} orders`, name]} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-[240px] flex items-center justify-center text-slate-400">No data</div>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Pending Orders by Urgency</CardTitle>
          </CardHeader>
          <CardContent>
            {urgencyData.some(d => d.count > 0) ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={urgencyData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={130} style={{ fontSize: '11px' }} />
                  <Tooltip formatter={(value) => [`${value} orders`, 'Count']} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
                  <Bar dataKey="count" label={{ position: 'right', fontSize: 11 }}>
                    {urgencyData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[240px] flex items-center justify-center text-slate-400">No pending orders</div>}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Vendor Performance + Lead Time Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendor Delivery Performance */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Vendor Delivery Performance</CardTitle>
            <CardDescription>Click a vendor bar to drill down</CardDescription>
          </CardHeader>
          <CardContent>
            {vendorPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(200, vendorPerformance.length * 45)}>
                <BarChart data={vendorPerformance} layout="vertical" onClick={(e) => {
                  if (e?.activeLabel) setDrillVendor(drillVendor === e.activeLabel ? null : e.activeLabel);
                }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="vendor" type="category" width={120} style={{ fontSize: '11px', cursor: 'pointer' }} />
                  <Tooltip
                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm" style={{ zIndex: 9999 }}>
                          <p className="font-semibold text-slate-800 mb-1 text-xs">{label}</p>
                          {payload.filter(p => p.value > 0).map((p, i) => (
                            <p key={i} className="text-xs" style={{ color: p.fill || p.color }}>
                              {p.name}: {p.value}
                            </p>
                          ))}
                          <p className="text-xs text-slate-500 mt-1 pt-1 border-t border-slate-100">
                            Total: {payload.reduce((s, p) => s + (p.value || 0), 0)}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                  <Bar dataKey="on_time" stackId="a" fill="#22c55e" name="On Time" />
                  <Bar dataKey="early" stackId="a" fill="#3b82f6" name="Early" />
                  <Bar dataKey="delayed" stackId="a" fill="#ef4444" name="Delayed" />
                  <Bar dataKey="pending" stackId="a" fill="#eab308" name="Pending" />
                  <Bar dataKey="at_risk" stackId="a" fill="#f97316" name="At Risk" />
                  <Bar dataKey="overdue" stackId="a" fill="#991b1b" name="Overdue" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[200px] flex items-center justify-center text-slate-400">No vendor data</div>}
          </CardContent>
        </Card>

        {/* Lead Time Trends */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Lead Time Trends (Weekly Avg)</CardTitle>
            <CardDescription>Average actual lead time and delivery variance</CardDescription>
          </CardHeader>
          <CardContent>
            {leadTimeTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={leadTimeTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" style={{ fontSize: '10px' }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis style={{ fontSize: '11px' }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm" style={{ zIndex: 9999 }}>
                          <p className="font-semibold text-slate-800 mb-1 text-xs">Week of {label}</p>
                          {payload.map((p, i) => (
                            <p key={i} className="text-xs" style={{ color: p.stroke || p.color }}>
                              {p.name === 'avg_lead_time' ? 'Avg Lead Time' : 'Avg Variance'}: {p.value} days
                            </p>
                          ))}
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="avg_lead_time" stroke="#3b82f6" strokeWidth={2} name="Avg Lead Time" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="avg_variance" stroke="#ef4444" strokeWidth={2} name="Avg Variance" dot={{ r: 3 }} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="h-[240px] flex items-center justify-center text-slate-400">No completed deliveries for trend analysis</div>}
          </CardContent>
        </Card>
      </div>

      {/* Vendor Drill-Down Banner */}
      {drillVendor && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium text-blue-800">Showing orders for: {drillVendor}</span>
            <Badge className="bg-blue-100 text-blue-700">{filteredOrders.length} orders</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDrillVendor(null)} data-testid="clear-vendor-drill">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to All
          </Button>
        </div>
      )}

      {/* Orders Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Order Details ({filteredOrders.length} orders)</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>MRO / Shipment</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead>Need Date</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Lead Time</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order, index) => {
                  const sc = STATUS_COLORS[order.status] || { bg: 'bg-gray-100', text: 'text-gray-800' };
                  return (
                    <TableRow key={index} data-testid={`order-row-${index}`}>
                      <TableCell>
                        <Badge className={`${sc.bg} ${sc.text}`}>{order.status.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{order.po_number}</div>
                        <div className="text-xs text-slate-400">{order.shipment_number}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{order.item_name}</div>
                        <div className="text-xs text-slate-400">{order.item_code}</div>
                      </TableCell>
                      <TableCell className="text-sm">{order.vendor_name}</TableCell>
                      <TableCell className="text-sm">{order.qty || '-'}</TableCell>
                      <TableCell className="text-sm">{order.order_date}</TableCell>
                      <TableCell className="text-sm font-medium">{order.need_date}</TableCell>
                      <TableCell className="text-sm">
                        {order.received_date || <span className="text-slate-400 italic">{order.shipment_status || 'Pending'}</span>}
                      </TableCell>
                      <TableCell className="text-sm">{order.lead_time_days != null ? `${order.lead_time_days}d` : '-'}</TableCell>
                      <TableCell>
                        <span className="text-sm">{order.status_message}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {filteredOrders.length === 0 && (
              <div className="text-center py-8 text-slate-400">No orders match the selected filters.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderTrackingDashboard;
